import React, { useState, useMemo } from "react";
import { Modal, Icon } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import "./NotificationHistoryModal.scss";

const LEVEL_COLORS = {
  1: { color: "#f59e0b", bg: "#fef3c7" },
  2: { color: "#e67e22", bg: "#fef5e9" },
  3: { color: "#c0392b", bg: "#fdecea" },
};

function daysDiff(dueDate) {
  if (!dueDate) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
  return Math.round((due - now) / 86400000);
}

function computeEscalationLevel(item) {
  if (["closed", "cancelled"].includes(item.status)) return 0;
  const diff = daysDiff(item.dueDate);
  if (diff === null) return 0;
  const l1 = item.rule_l1 ?? 0;
  const l2 = item.rule_l2 ?? 3;
  const l3 = item.rule_l3 ?? 7;
  const overdue = -diff;
  if (overdue >= l3) return 3;
  if (overdue >= l2) return 2;
  if (diff <= l1)    return 1;
  return 0;
}

function interpolate(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");
}

function buildNotifications(items, t) {
  const entries = [];
  const now = new Date();

  items.forEach((item) => {
    const level = computeEscalationLevel(item);
    if (level === 0) return;

    const id        = `AC-${item._id?.toString().slice(-6).toUpperCase()}`;
    const overdue   = Math.abs(daysDiff(item.dueDate) ?? 0);
    const prioLabel = t(`priority_${item.priority}_short`) || item.priority || t("nhm_resp2_default");
    const progress  = item.progress ?? 0;

    const resp1      = item.resp1 || item.responsible || t("nhm_resp_default");
    const resp1Cargo = t("nhm_resp1_cargo");
    const resp1Email = item.resp1_email || "";

    ["email", "nativa"].forEach((canal) => {
      entries.push({
        _key:        `${item._id}-L1-${canal}`,
        fecha:       now,
        accionId:    id,
        accionTitle: item.title,
        nivel:       1,
        destinatario: resp1,
        cargo:       resp1Cargo,
        email:       resp1Email,
        canal,
        mensaje:     interpolate(t("nhm_msg_l1"), { title: item.title, id, overdue, progress, priority: prioLabel }),
      });
    });

    if (level >= 2) {
      const resp2      = item.resp2 || t("nhm_resp2_default");
      const resp2Cargo = t("nhm_resp2_cargo");
      const resp2Email = item.resp2_email || "";
      ["email", "nativa"].forEach((canal) => {
        entries.push({
          _key:        `${item._id}-L2-${canal}`,
          fecha:       now,
          accionId:    id,
          accionTitle: item.title,
          nivel:       2,
          destinatario: resp2,
          cargo:       resp2Cargo,
          email:       resp2Email,
          canal,
          mensaje:     interpolate(t("nhm_msg_l2"), { title: item.title, id, overdue, resp1, priority: prioLabel }),
        });
      });
    }

    if (level >= 3) {
      const resp3      = item.resp3 || t("nhm_resp3_default");
      const resp3Cargo = t("nhm_resp3_cargo");
      const resp3Email = item.resp3_email || "";
      ["email", "nativa"].forEach((canal) => {
        entries.push({
          _key:        `${item._id}-L3-${canal}`,
          fecha:       now,
          accionId:    id,
          accionTitle: item.title,
          nivel:       3,
          destinatario: resp3,
          cargo:       resp3Cargo,
          email:       resp3Email,
          canal,
          mensaje:     interpolate(t("nhm_msg_l3"), { title: item.title, id, overdue, priority: prioLabel }),
        });
      });
    }
  });

  return entries;
}

function exportHistoryCSV(notifications, t) {
  const headers = [t("nhm_col_date"), t("nhm_col_action"), t("nhm_col_level"), t("nhm_col_recipient"), t("nhm_email"), t("nhm_col_channel"), t("nhm_col_message")];
  const rows = notifications.map((n) => [
    n.fecha.toLocaleString("es-AR"),
    n.accionId,
    `${t("nhm_level_label")} ${n.nivel}`,
    `${n.destinatario} (${n.cargo})`,
    n.email,
    n.canal === "email" ? t("nhm_email") : t("nhm_native_notif"),
    n.mensaje,
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = "historial_notificaciones.csv"; a.click();
  URL.revokeObjectURL(url);
}

export function NotificationHistoryModal({ show, close, items = [] }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [search,      setSearch]      = useState("");
  const [filterNivel, setFilterNivel] = useState("");
  const [filterCanal, setFilterCanal] = useState("");

  const notifications = useMemo(() => buildNotifications(items, t), [items, translations]);

  const filtered = useMemo(() => {
    let list = notifications;
    if (filterNivel) list = list.filter((n) => n.nivel === Number(filterNivel));
    if (filterCanal) list = list.filter((n) => n.canal === filterCanal);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (n) =>
          n.destinatario.toLowerCase().includes(q) ||
          n.mensaje.toLowerCase().includes(q) ||
          n.accionId.toLowerCase().includes(q)
      );
    }
    return list;
  }, [notifications, filterNivel, filterCanal, search]);

  const stats = useMemo(() => ({
    total:   notifications.length,
    l1:      notifications.filter((n) => n.nivel === 1).length,
    l2:      notifications.filter((n) => n.nivel === 2).length,
    l3:      notifications.filter((n) => n.nivel === 3).length,
    preavis: 0,
    email:   notifications.filter((n) => n.canal === "email").length,
    nativa:  notifications.filter((n) => n.canal === "nativa").length,
  }), [notifications]);

  const fmt = (d) => d.toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  return (
    <Modal
      open={show}
      onClose={close}
      closeOnDimmerClick={false}
      centered={false}
      className="nhm-modal"
    >
      <div className="nhm">

        {/* ── Header ── */}
        <div className="nhm__header">
          <span className="nhm__header-title">{t("nhm_title")}</span>
          <button className="nhm__header-close" onClick={close}>
            <Icon name="close" />
          </button>
        </div>

        <div className="nhm__body">

          {/* ── Summary bar ── */}
          <div className="nhm__summary">
            <span><strong>{stats.total}</strong> {t("nhm_total")}</span>
            <span className="nhm__sum-sep" />
            <span>{t("nhm_level_label")} 1: <strong>{stats.l1}</strong></span>
            <span>{t("nhm_level_label")} 2: <strong>{stats.l2}</strong></span>
            <span>{t("nhm_level_label")} 3: <strong>{stats.l3}</strong></span>
            <span>{t("nhm_pre_notices")}: <strong>{stats.preavis}</strong></span>
            <span className="nhm__sum-sep" />
            <span><Icon name="mail outline" />{t("nhm_email")}: <strong>{stats.email}</strong></span>
            <span><Icon name="desktop" />{t("nhm_native_notif")}: <strong>{stats.nativa}</strong></span>
          </div>

          {/* ── Filters ── */}
          <div className="nhm__filters">
            <div className="nhm__filter-group">
              <span className="nhm__filter-label">{t("nhm_search_label")}</span>
              <input
                className="nhm__search"
                placeholder={t("nhm_search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="nhm__filter-group">
              <span className="nhm__filter-label">{t("nhm_level_label")}</span>
              <select className="nhm__select" value={filterNivel} onChange={(e) => setFilterNivel(e.target.value)}>
                <option value="">{t("nhm_all")}</option>
                <option value="1">{t("nhm_level_label")} 1</option>
                <option value="2">{t("nhm_level_label")} 2</option>
                <option value="3">{t("nhm_level_label")} 3</option>
              </select>
            </div>
            <div className="nhm__filter-group">
              <span className="nhm__filter-label">{t("nhm_canal_label")}</span>
              <select className="nhm__select" value={filterCanal} onChange={(e) => setFilterCanal(e.target.value)}>
                <option value="">{t("nhm_all")}</option>
                <option value="email">{t("nhm_email")}</option>
                <option value="nativa">{t("nhm_native_notif")}</option>
              </select>
            </div>
          </div>

          {/* ── Table ── */}
          <div className="nhm__table-wrap">
            <table className="nhm__tbl">
              <thead>
                <tr>
                  <th>{t("nhm_col_date")}</th>
                  <th>{t("nhm_col_action")}</th>
                  <th>{t("nhm_col_level")}</th>
                  <th>{t("nhm_col_recipient")}</th>
                  <th>{t("nhm_col_channel")}</th>
                  <th>{t("nhm_col_message")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="nhm__empty">{t("nhm_empty")}</td>
                  </tr>
                ) : filtered.map((n) => {
                  const colors = LEVEL_COLORS[n.nivel];
                  return (
                    <tr key={n._key}>
                      <td className="nhm__td-date">{fmt(n.fecha)}</td>
                      <td className="nhm__td-id">{n.accionId}</td>
                      <td>
                        <span className="nhm__level-badge" style={{ background: colors?.color }}>
                          {t("nhm_level_label")} {n.nivel}
                        </span>
                      </td>
                      <td className="nhm__td-dest">
                        <span className="nhm__dest-name">{n.destinatario}</span>
                        {n.cargo && <span className="nhm__dest-cargo">({n.cargo})</span>}
                        {n.email && <span className="nhm__dest-email">{n.email}</span>}
                      </td>
                      <td>
                        {n.canal === "email" ? (
                          <span className="nhm__canal"><Icon name="mail outline" />{t("nhm_email")}</span>
                        ) : (
                          <span className="nhm__canal nhm__canal--nativa"><Icon name="desktop" />{t("nhm_native")}</span>
                        )}
                      </td>
                      <td className="nhm__td-msg">{n.mensaje}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Footer ── */}
          <div className="nhm__footer">
            <button className="nhm__export-btn" onClick={() => exportHistoryCSV(filtered, t)}>
              <Icon name="download" />{t("nhm_export_csv")}
            </button>
            <button className="nhm__close-btn" onClick={close}>{t("nhm_close")}</button>
          </div>

        </div>
      </div>
    </Modal>
  );
}
